import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ParcelService } from '../../shared/services/parcel.service';
import { MessageService } from 'primeng/api';
import { DataService } from '../../shared/services/data.service';
import { SpinnerComponent } from "../../shared/components/spinner/spinner.component";
import { SearchComponent } from "../../shared/components/search-component/search-component.component";
import { FileViewerComponent } from "../../shared/components/file-viewer/file-viewer.component";
import { PRIMENG_MODULES } from '../../shared/primeng-imports';


@Component({
  selector: 'app-search-parcel-terminal',
  templateUrl: './terminal.component.html',
  styleUrl: './terminal.component.scss',
  standalone: true,
  imports: [...PRIMENG_MODULES, SpinnerComponent, FileViewerComponent]
})
export class TerminalComponent implements OnChanges {
  @Input() hideOwnSearchBar: boolean = false;
  @Input() viewerActive: boolean = true;
  @Output() searchCleared = new EventEmitter<void>();
  @Output() searchResult = new EventEmitter<boolean>();
  data: any;
  images!: any[];
  groupedData: { [key: string]: { [key: string]: any[] } } = {};
  selectedImageName: string = '';
  selectedImageUrl: string = '';
  itemId: string | undefined;
  isLoading: boolean = false;
  currentImage: any;
  isBestImage: boolean = false;
  isDataNotFound: boolean = false;
  isImageNotFound: boolean = false;
  products: any;
  expandedRows: { [s: string]: boolean } = {};
  first = 0;
  rows = 5;
  showRightPanel: boolean = true;
  
  constructor(private parcelService: ParcelService,
    private messageService: MessageService,
    private dataService: DataService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['viewerActive']?.currentValue) {
      this.publishPrintData();
    }
  }


  searchSmartStoreMetatadata() {
    if (!this.itemId) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter item id.' });
      return;
    }
    this.resetImage();
    this.resetUI();
    this.isDataNotFound = false;
    this.isLoading = true;
    this.data = null;

    this.parcelService.getSmartstoreMetadata(this.itemId).subscribe({
      next: (result) => {

        this.isLoading = false;
        if (result && result?.images) {
          this.data = result
          this.images = this.data.images;

          let tableData = this.groupByDateAndTerminalTest(this.data.images);
          let count = 1
          this.products = Object.entries(tableData).map(([timestamp, terminals]) => ({
            id: count++,
            timestamp,
            terminals: Object.entries(terminals as Record<string, any[]>).map(([terminal, images]) => ({
              terminal,
              images
            }))
          }));

          this.products.forEach((p: any) => {
            this.expandedRows[p.id] = true;
          });
          this.groupedData = this.groupByDateAndTerminal();

          this.currentImage = this.images[0];
          this.isBestImage = true;
          this.getImageFromSmartStore("bestImage");
          this.searchResult.emit(true);
        } else {
          this.isDataNotFound = true;
          this.resetUI();
          // this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Item not found. Please try again.' });
          this.searchResult.emit(false);
        }
      },
      error: (err) => {
        
        this.isLoading = false;
        this.isDataNotFound = true;
        this.resetUI();
        const errorMessage = err?.error === 'Failed to get document from DocumentAPI: Connection refused (document-api:8080)'
          ? 'Smart Store is temporarily unavailable. Please try again later.'
          : err?.error || 'An error occurred while fetching data from Smart Store. Please try again.';
        // this.messageService.add({ severity: 'warn', summary: 'Warning', detail: errorMessage });
        this.searchResult.emit(false);
      }
    });
  }
  groupByDateAndTerminalTest(items: any[]) {
    const result: any = {};

    for (let item of items) {
      const { timestamp, terminal } = item;

      if (!result[timestamp]) {
        result[timestamp] = {};
      }
      if (!result[timestamp][terminal]) {
        result[timestamp][terminal] = [];
      }

      result[timestamp][terminal].push(item);
    }

    return result;
  }
  resetUI() {
    this.images = [];
    this.groupedData = {};
    this.products = {}
    this.currentImage = '';
    this.isBestImage = false;
    this.data = null;
    this.resetImage();
    this.clearPrintData();
  }

  resetImage() {
    this.selectedImageName = '';
    this.selectedImageUrl = '';
  }

  getImageFromSmartStore(requestType: string) {
    this.isBestImage = false;
    if (requestType === 'bestImage' || requestType === 'originalImage')
      this.isBestImage = true;
    this.isLoading = true;
    this.isImageNotFound = false;
    this.parcelService.getSmartStoreImage(this.data.id, requestType, this.currentImage.id, 'ORIGINAL', 'blob').subscribe({
      next: (result) => {
        const blob = new File(
          [result],
          this.currentImage.id + '.jpg',
          { type: "image/jpeg" }
        );
        this.selectedImageName = this.currentImage.id + '.jpg';
        this.selectedImageUrl = URL.createObjectURL(blob);
        this.isLoading = false;
        this.publishPrintData();
      },
      error: (err) => {
        if (requestType == "bestImage" && (err.status == 404 || err.status == 400 || err.status == 500)) {
          this.getImageFromSmartStore("originalImage");
        }
        else {
          this.isLoading = false;
          this.isImageNotFound = true;
          this.resetImage();
          let errMsg = err.error.message;
          if (!errMsg) {
            errMsg = 'Requested image is temporarily not available'
          }
          this.messageService.add({ severity: 'warn', summary: 'Warning', detail: errMsg });
        }
      }
    });
  }

  selectImage(event: MouseEvent, image: any) {
    event.preventDefault();
    this.currentImage = image;
    this.isBestImage = false;
    this.getImageFromSmartStore("specifiedImage");
  }

  groupByDateAndTerminal() {
    return this.images.reduce((acc: any, image: any) => {
      const date = image.timestamp;
      acc[date] ??= {};
      acc[date][image.terminal] ??= [];
      acc[date][image.terminal].push(image);

      return acc;
    }, {});
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }
  get sortedDates(): string[] {
    return Object.keys(this.groupedData).sort((a, b) => b.localeCompare(a));
  }

  onSearchParcel(value: any) {
    this.isImageNotFound = false;
    this.itemId = value;
    if (value != '') {
      this.searchSmartStoreMetatadata();
    } else {
      this.isImageNotFound = false;
      this.resetUI();
      this.searchCleared.emit();
    }
  }

  ngOnDestroy(): void {
    this.clearPrintData();
  }

  private publishPrintData(): void {
    if (!this.viewerActive) {
      return;
    }

    this.dataService.updatePrintData({
      selectedFileName: this.selectedImageName,
      selectedFileUrl: this.selectedImageUrl
    });
  }

  private clearPrintData(): void {
    if (this.viewerActive) {
      this.dataService.updatePrintData({});
    }
  }
}
